import { Language, LocalStorageKey } from '@common/enums/global.enums'
import { messages } from '@config/lang/messages'
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { IntlProvider } from 'react-intl'

type LanguageContextType = {
	locale: Language
	switchLanguage: (locale: Language) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const LanguageProvider = ({ children }: PropsWithChildren) => {
	const [locale, setLocale] = useState<Language>(Language.EN)

	const switchLanguage = useCallback((newLocale: Language) => {
		if (messages[newLocale]) {
			setLocale(newLocale)
			localStorage.setItem(LocalStorageKey.SELECTED_LANGUAGE, newLocale)
		}
	}, [])

	const value = useMemo((): LanguageContextType => ({
		locale,
		switchLanguage
	}), [
		locale,
		switchLanguage
	])

	useEffect(() => {
		const saved = localStorage.getItem(LocalStorageKey.SELECTED_LANGUAGE) as Language

		if (saved && messages[saved]) {
			setLocale(saved)
		} else {
			const browserLang = navigator.language.split('-')[0] as Language
			const finalLang = messages[browserLang] ? browserLang : Language.EN

			setLocale(finalLang)
			localStorage.setItem(LocalStorageKey.SELECTED_LANGUAGE, finalLang)
		}
	}, [])

	return (
		<LanguageContext.Provider value={value}>
			<IntlProvider locale={locale} defaultLocale={Language.EN} messages={messages[locale]}>
				{children}
			</IntlProvider>
		</LanguageContext.Provider>
	)
}

/**
 * Language context
 */
export const useLanguage = () => {
	const context = useContext(LanguageContext)
	if (!context) throw new Error('useLanguage must be used within a LanguageProvider')
	return context
}
